import _ from "lodash";
import {
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_UBD,
  ELEMENT_TYPE_UBD_SUBJECT,
  ELEMENT_TYPE_MYP_OBJECTIVES,
  ELEMENT_TYPE_MYP_SUBJECT,
  ELEMENT_TYPE_DP_SUBJECT,
  ELEMENT_TYPE_PYP_BENCHMARK,
} from "Constants/stringConstants";
import {
  CUSTOM_CRITERIA_TYPES_FOR_EE_RP_PROJECTS,
  DYNAMIC_CRITERIA_TYPES,
  DYNAMIC_CRITERIA_TYPES_FOR_CAS_SL_PROJECTS,
  DYNAMIC_CRITERIA_TYPES_FOR_CP_PROJECTS,
  DYNAMIC_CRITERIA_TYPES_FOR_EE_RP_PROJECTS,
  DYNAMIC_CRITERIA_TYPES_FOR_PP_PROJECTS,
  DYNAMIC_CRITERIA_TYPES_FOR_SA_PROJECTS,
  DYNAMIC_CRITERIA_TYPES_FOR_TOK_PROJECTS,
  DYNAMIC_CRITERIA_TYPES_FOR_TOK_OVERALL_PROJECTS,
  GPA_ASSOCIATED_CRITERIA_TYPES,
  GPA_ASSOCIATED_CRITERIA_TYPES_EXCLUDED_FROM_SUBJECT_LEVEL_CRITERIA_SET,
  IB_DEFINED,
  LEARNING_OUTCOME_CRITERIA_TYPE,
  MYP_CRITERIA_FINAL_SCORE,
  achievementCriterionLabels,
  criteriaTypesForCumulativeReport,
  projectCriteriaTypes,
  pypElementMapping,
} from "./constants";
import { shouldHideAggregationRowOrColumnInTemplate } from "./templateHelpers";
import { getValueBasedOnReportType } from "./reportTypeHelpers";
import {
  getIsEnableFinalGradeForIL,
  getIsMYPCriteriaTotalAsAggregateEnabled,
} from "./featureFlags";
import { getStudentElementRatingTotal } from "./gradeAndRating";
import {
  getStudentElementRatingOfCriteriaSet,
  getStudentElementRatingValueOfCriteriaSet,
  transformGradeScaleValuesToCriteriaValues,
} from "./ratingPrimitives";

export {
  getStudentElementRatingOfCriteriaSet,
  getStudentElementRatingValueOfCriteriaSet,
  transformGradeScaleValuesToCriteriaValues,
};

const getUpdatedGradeNode = ({
  gradeNode,
  criterion,
  curriculumType,
  nodeId,
  nodes,
  termId,
  isSingleTermReport,
  nodeUid,
  isMYPCriteriaObjective,
}) => {
  const isNewReportingSetup = getValueBasedOnReportType();
  const criteriaType = criterion?.type || criterion?.criteriaType;
  const isCustomObjectiveCriteria =
    isMYPCriteriaObjective &&
    (isNewReportingSetup
      ? criterion?.type !== "MYP_IB_DEFINED"
      : criterion?.criteriaType == "CUSTOM");

  if (
    (_.includes(["IB_DEFINED", "MYP_IB_DEFINED"], criteriaType) &&
      curriculumType == CURRICULUM_TYPE_MYP) ||
    isCustomObjectiveCriteria
  ) {
    const objectiveNode = _.find(nodes, {
      uid:
        nodeUid == "INTERDISCIPLINARY_LEARNING"
          ? "INTERDISCIPLINARY_OBJECTIVES"
          : "SUBJECT_OBJECTIVES",
      parent: nodeId,
    });
    const termPhaseId = isSingleTermReport
      ? _.get(objectiveNode, "moreDetails.mypCriteria.id", null)
      : _.get(
          objectiveNode,
          ["moreDetails", "mypCriteriaTermMap", termId, "id"],
          null
        );

    const objectivesChildren = _.get(objectiveNode, "children", []);

    const objectiveLabel = criterion?.objectiveLabel;

    const criterionId = _.find(objectivesChildren, child => {
      const objectiveChildNode = nodes?.[child] || {};
      return (
        _.includes(objectiveChildNode?.label, objectiveLabel) &&
        (isCustomObjectiveCriteria ||
          objectiveChildNode?.mypCriteriaId == termPhaseId)
      );
    });

    return nodes?.[criterionId];
  } else {
    return curriculumType == CURRICULUM_TYPE_PYP
      ? _.get(nodes, [nodeId], {})
      : gradeNode;
  }
};

const AGGREGATE_SUB_TYPE_DISPLAY_SEQUENCE = {
  SUM: 1,
  AVERAGE: 2,
};

export const getCriteriaSetOfPypType = ({
  type,
  subType = null,
  parentId = null,
  levelId = null,
  academicCriteriaSets = [],
  ratingLevels = [],
}) => {
  /*******************************************************************************************************
   * Logic:
   *       Parent id mostly comes as string but it can come as array in-case of non-pyp subject atls
   *       or in case of myp il atls, therefore we are casting it to array and checking if it matches
   *       level.ibPYPParentElementId.
   ********************************************************************************************************/
  const hasSubType = !!subType;
  const hasParent = !!parentId;
  const hasLevel = !!levelId;

  const levelIdSet = hasLevel
    ? new Set(_.map(_.castArray(levelId), String))
    : null;

  const parentIdSet = hasParent
    ? new Set(_.map(_.castArray(parentId), String))
    : null;

  const criteriaSetIdSet = new Set();

  _.forEach(ratingLevels, level => {
    if (level.ibPYPElementType != type) return true;

    if (hasSubType && level.subType != subType) return true;

    if (hasLevel && !levelIdSet.has(String(level.levelId))) return true;

    if (hasParent && !parentIdSet.has(String(level.ibPYPParentElementId)))
      return true;

    const academicCriteriaSetIds = _.get(level, "academicCriteriaSets", []);

    _.forEach(academicCriteriaSetIds, acsId => {
      criteriaSetIdSet.add(acsId);
    });
  });

  const result = [];

  _.forEach(academicCriteriaSets, criteriaSet => {
    if (!criteriaSetIdSet.has(criteriaSet.id)) return true;

    if (
      _.isUndefined(criteriaSet.isRatingEnabled) ||
      criteriaSet.isRatingEnabled
    ) {
      result.push(criteriaSet);
    }
  });

  return result;
};

//To handle updating of psNode isEnabled alongside the node's isEnabled property.

export const checkCriteriaSetOfPypTypeInLevels = ({
  type,
  subType = null,
  parentId = null,
  levelId = "L1",
  setId,
  ratingLevels,
}) => {
  const criteriaSetIdSet = new Set();
  const ibPYPElementTypeSet = new Set(_.map(_.castArray(type), String));

  _.forEach(ratingLevels, level => {
    if (!ibPYPElementTypeSet.has(String(level.ibPYPElementType))) return true;

    if (subType && level.subType != subType) return true;

    if (parentId && level.ibPYPParentElementId != parentId) return true;

    if (levelId && level.levelId != levelId) return true;

    const academicCriteriaSetIds = _.get(level, "academicCriteriaSets", []);
    _.forEach(academicCriteriaSetIds, acsId => {
      criteriaSetIdSet.add(acsId);
    });
  });

  return criteriaSetIdSet.has(setId);
};

export const getObjectivesCriteriaSet = ({
  academicCriteriaSets,
  isViewModeReportEnd,
  showObjectivesCriteriaInCumulative,
  objectivesChildren,
  nodes,
  t,
  nodeUid,
  isNewGradingSetup,
  gradeScales,
}) => {
  const mypObjectiveCriteriaSet = isNewGradingSetup
    ? _.find(
        gradeScales,
        item => "MYP_IB_DEFINED" == item?.type || item?.criteriaType
      )
    : _.find(academicCriteriaSets, { criteriaType: "IB_DEFINED" }) || {};

  const showCriteriaNodeLabel =
    isViewModeReportEnd && nodeUid != "PROGRESS_SUMMARY_SUBJECTS";

  return showObjectivesCriteriaInCumulative
    ? _.reduce(
        achievementCriterionLabels,
        (acc, item) => {
          const { label, objectiveLabel } = item;

          const criterionNodeId = _.find(objectivesChildren, childId => {
            return _.includes(
              _.get(nodes, [childId, "label"], ""),
              objectiveLabel
            );
          });

          const criterionNode = _.get(nodes, criterionNodeId, {});

          //show objective criteria set only if there is rating available for it in any term in locked reports.
          const showCriteria = isViewModeReportEnd ? !!criterionNodeId : true;

          if (showCriteria) {
            const defaultCriteriaLabel = t(
              "progressReport:criterion_with_label",
              {
                label,
              }
            );

            acc = [
              ...acc,
              {
                ...mypObjectiveCriteriaSet,
                label: showCriteriaNodeLabel
                  ? _.get(criterionNode, "label", defaultCriteriaLabel)
                  : defaultCriteriaLabel,
                objectiveLabel,
                nodeId: criterionNodeId,
              },
            ];
          }

          return acc;
        },
        []
      )
    : [];
};

const getIsTotalAvailableForCriteriaTerm = ({
  itemId,
  ratings,
  subjectObjectivesMap,
  academicCriteriaSets,
  gradingPeriodType,
  isViewModeReportEnd,
  isFreezed,
  showObjectivesCriteriaInCumulative,
  termIds,
  calculateMYPCriteriaTotalMethod,
  nodeUid,
  objectivesCriteriaSet,
}) => {
  const isMYPCriteriaTotalAsAggregateEnabled = getIsMYPCriteriaTotalAsAggregateEnabled();

  /* For multi term report we are keeping the behavior same as term report so therefore total will not be shown in
  in progress summary  */
  // Only if isMYPCriteriaTotalAsAggregateEnabled is false then we will not show total in progress summary.
  if (
    nodeUid == "PROGRESS_SUMMARY_SUBJECTS" &&
    (!isMYPCriteriaTotalAsAggregateEnabled || _.isEmpty(objectivesCriteriaSet))
  ) {
    return false;
  }

  //In locked reports remove total if there aren't complete ratings available for A,B,C and D criteria for any term.
  return isViewModeReportEnd && isFreezed && showObjectivesCriteriaInCumulative
    ? _.some(termIds, termId => {
        const total = getStudentElementRatingTotal({
          itemId,
          ratings,
          subjectObjectivesMap,
          academicCriteriaSets,
          gradingPeriodType,
          termId,
          returnZeroValue: true,
          calculateMYPCriteriaTotalMethod,
          uid: nodeUid,
        });

        return !_.isNaN(+total);
      })
    : true; //so in template end or unlocked report end we always show it.
};

export const filteredCriteriaSetsForCumulativeReport = ({
  academicCriteriaSets,
  t,
  curriculumProgramType,
  showObjectivesCriteria,
  viewType,
  objectivesChildren,
  nodes,
  itemId,
  ratings,
  subjectObjectivesMap,
  gradingPeriodType,
  termIds,
  isFreezed,
  subjectType,
  ratingLevels,
  type,
  isFromProgressReport = false,
  calculateMYPCriteriaTotalMethod,
  nodeUid,
  levelId = "0",
  criteriaType,
  subType,
  allowedCustomCriteria,
  fromSubjectView,
}) => {
  const showObjectivesCriteriaInCumulative =
    curriculumProgramType == CURRICULUM_TYPE_MYP &&
    showObjectivesCriteria &&
    // Check for if the criteria is present in allowed criteria type
    (!criteriaType || _.includes(criteriaType, "IB_DEFINED"));

  const isViewModeReportEnd = viewType == "REPORT_END";

  //Collect Objective Criteria A,B,C and D for MYP Cumulative Report.
  const objectivesCriteriaSet = getObjectivesCriteriaSet({
    academicCriteriaSets,
    isViewModeReportEnd,
    showObjectivesCriteriaInCumulative,
    objectivesChildren,
    nodes,
    t,
    nodeUid,
  });

  //Get if any term in cumulative report will have a total available.
  const isTotalAvailableForCriteriaTerm = getIsTotalAvailableForCriteriaTerm({
    itemId,
    ratings,
    subjectObjectivesMap,
    academicCriteriaSets,
    gradingPeriodType,
    isViewModeReportEnd,
    isFreezed,
    showObjectivesCriteriaInCumulative,
    termIds,
    calculateMYPCriteriaTotalMethod,
    nodeUid,
    objectivesCriteriaSet,
  });

  const isCriteriaSetIncluded = criteriaSet => {
    if (
      criteriaSet?.criteriaType == "MYP_CRITERIA_FINAL_SCORE" &&
      (!showObjectivesCriteriaInCumulative || !isTotalAvailableForCriteriaTerm)
    ) {
      return false;
    }

    const updatedCriteriaTypesForCumulativeReport = [
      ...(!criteriaType
        ? criteriaTypesForCumulativeReport
        : _.without(criteriaType, "IB_DEFINED")),
      "CUSTOM",
    ];

    let isCriteriaMappedToSubject = true;
    if (isFromProgressReport && criteriaSet.criteriaType == "CUSTOM") {
      const isExcludedNode = _.includes(
        ["SUBJECT_OBJECTIVES", "INTERDISCIPLINARY_OBJECTIVES"],
        nodeUid
      );
      const isFilteredOutInLockedReport =
        !_.includes(allowedCustomCriteria, criteriaSet.id) &&
        isFreezed &&
        isFromProgressReport;

      isCriteriaMappedToSubject =
        isExcludedNode || isFilteredOutInLockedReport
          ? false
          : checkCriteriaSetOfPypTypeInLevels({
              type,
              levelId,
              parentId: itemId,
              setId: criteriaSet?.id,
              ratingLevels,
              subType,
            });
    }

    const isCriteriaSetEnabled =
      nodeUid == "PROGRESS_SUMMARY_SUBJECTS"
        ? criteriaSet.showInProgressSummary
        : criteriaSet?.isRatingEnabled;

    const isGpaType = _.includes(
      GPA_ASSOCIATED_CRITERIA_TYPES,
      criteriaSet.criteriaType
    );

    return (
      isCriteriaSetEnabled &&
      _.includes(
        updatedCriteriaTypesForCumulativeReport,
        criteriaSet.criteriaType
      ) &&
      isCriteriaMappedToSubject &&
      !_.includes(["CATEGORY_GRADE_SCALE"], criteriaSet.subType) &&
      (criteriaSet.criteriaType == "FINAL_GRADE"
        ? (subjectType == "TOK" && criteriaSet.subType == "TOK") ||
          (subjectType != "TOK" && criteriaSet.subType != "TOK")
        : true) &&
      (fromSubjectView ? !isGpaType : true) &&
      !((subjectType == "TOK" || nodeUid == "SUBJECT_GRADES") && isGpaType)
    );
  };

  return [
    ...objectivesCriteriaSet,
    ..._.filter(academicCriteriaSets, isCriteriaSetIncluded),
  ];
};

const getFilteredAndUpdatedCriteriaSetsForSubjects = ({
  filteredAcademicCriteriaSets,
  nodes,
  curriculumType,
  ratingLevels,
  showOverallGradeForIl,
  showLocalGradeForIl,
  isMYPCriteriaTotalAsAggregateEnabled,
  t,
}) => {
  const subjectNodes = _.filter(
    nodes,
    item =>
      _.includes(
        ["PS_BENCHMARK", "SUBJECT", "INTERDISCIPLINARY_LEARNING"],
        item?.uid
      ) && _.get(item, "psNode.isEnabled", false)
  );

  const filteredSubjectCriteria = _.filter(
    filteredAcademicCriteriaSets,
    set => {
      let showCriteria = _.reduce(
        subjectNodes,
        (acc, { itemId, uid }) => {
          const isInterdisciplinary = uid == "INTERDISCIPLINARY_LEARNING";

          const ilCriteriaDefaultValue =
            (getIsEnableFinalGradeForIL() &&
              _.includes(
                getIlDynamicCriteriaTypes({
                  showOverallGradeForIl,
                  showLocalGradeForIl,
                }),
                set?.criteriaType
              )) ||
            set?.criteriaType == "MYP_CRITERIA_FINAL_SCORE";

          const defaultCriteriaValue = isInterdisciplinary
            ? ilCriteriaDefaultValue
            : true;

          const mypTypes = isInterdisciplinary
            ? [ELEMENT_TYPE_MYP_OBJECTIVES]
            : [ELEMENT_TYPE_MYP_SUBJECT];

          const showCriteria = _.isEqual("CUSTOM", set?.criteriaType)
            ? checkCriteriaSetOfPypTypeInLevels({
                setId: set.id,
                levelId: curriculumType == CURRICULUM_TYPE_PYP ? "L0" : "0",
                type: [
                  ELEMENT_TYPE_UBD_SUBJECT,
                  ELEMENT_TYPE_DP_SUBJECT,
                  ELEMENT_TYPE_PYP_BENCHMARK,
                  ...mypTypes,
                ],
                ratingLevels,
                parentId: itemId,
              })
            : defaultCriteriaValue;

          if (showCriteria) {
            acc = true;
          }
          return acc;
        },
        false
      );

      if (curriculumType == CURRICULUM_TYPE_MYP) {
        showCriteria =
          showCriteria ||
          checkCriteriaSetOfPypTypeInLevels({
            setId: set.id,
            type: ELEMENT_TYPE_MYP_OBJECTIVES,
            ratingLevels,
            levelId: "2",
          });
      }

      //Learning outcome criteria will never be shown for subject.
      if (set.criteriaType === LEARNING_OUTCOME_CRITERIA_TYPE) {
        showCriteria = false;
      }

      return showCriteria;
    }
  );

  const excludedCriteriaTypes = _.union(
    DYNAMIC_CRITERIA_TYPES_FOR_PP_PROJECTS,
    DYNAMIC_CRITERIA_TYPES_FOR_CP_PROJECTS,
    DYNAMIC_CRITERIA_TYPES_FOR_EE_RP_PROJECTS,
    DYNAMIC_CRITERIA_TYPES_FOR_CAS_SL_PROJECTS,
    CUSTOM_CRITERIA_TYPES_FOR_EE_RP_PROJECTS,
    DYNAMIC_CRITERIA_TYPES_FOR_TOK_PROJECTS,
    [MYP_CRITERIA_FINAL_SCORE],
    GPA_ASSOCIATED_CRITERIA_TYPES_EXCLUDED_FROM_SUBJECT_LEVEL_CRITERIA_SET,
    ["PROGRESSION_POINT"]
  );

  const mypAchievementLevelCriteriaSet = getCriteriaSetBasedOnCriteriaType({
    academicCriteriaSets: filteredSubjectCriteria,
    criteriaType: IB_DEFINED,
  });

  const updatedCriteriaSetsForSubjects = _.reduce(
    filteredSubjectCriteria,
    (acc, criteriaSet) => {
      if (!_.includes(excludedCriteriaTypes, criteriaSet.criteriaType)) {
        acc.push(criteriaSet);
      }

      // Only show the total criteria set for MYP if the setting is enabled
      // the curriculum program type is MYP
      // A,B,C,D criterion are enabled in progress summary
      if (
        isMYPCriteriaTotalAsAggregateEnabled &&
        curriculumType == CURRICULUM_TYPE_MYP &&
        criteriaSet.criteriaType == MYP_CRITERIA_FINAL_SCORE &&
        mypAchievementLevelCriteriaSet?.showInProgressSummary
      ) {
        acc.push({
          ...criteriaSet,
          label: t("progressReport:criteria_total"),
        });
      }

      return acc;
    },
    []
  );

  return updatedCriteriaSetsForSubjects;
};

export const getFilteredAndUpdatedCriteriaSetsForSubjectsMemoized = _.memoize(
  params => getFilteredAndUpdatedCriteriaSetsForSubjects(params),
  ({
    filteredAcademicCriteriaSets,
    curriculumType,
    showOverallGradeForIl,
    showLocalGradeForIl,
    isMYPCriteriaTotalAsAggregateEnabled,
  }) => {
    const criteriaSetIds = _.map(filteredAcademicCriteriaSets, "id").join(",");
    return `${criteriaSetIds}|${curriculumType}|${showOverallGradeForIl}|${showLocalGradeForIl}|${isMYPCriteriaTotalAsAggregateEnabled}`;
  }
);

const getLevelsBasedOnUptoLevel = ({
  upToLevel = "3",
  prefix = "",
  startFromLevel = 1,
}) => {
  const maxLevel = parseInt(upToLevel) + 1;
  return _.map(_.range(startFromLevel, maxLevel), level => `${prefix}${level}`);
};

export const getLevelIdForCriteriaSet = ({ node, parentNode }) => {
  // Based on nodeUpToLevel getLevelIds
  const nodeUpToLevel = _.toString(node?.upToLevel);
  const updatedNodeUpToLevel = _.trimStart(nodeUpToLevel, "L");

  const isInterdisciplinaryMYPNode =
    node?.entityType == "INTERDISCIPLINARY_ATLS";

  // For template end we check parentNode Id
  // For report end we check pypType of node and its parent
  const isPsuAtlChildrenNode =
    parentNode?.id == "psuAtls" ||
    (node?.pypType == "atl" && parentNode?.pypType == "unitPlan");

  const isPsAtlsChildrenNode = parentNode?.id == "psAtls";

  const isPsuBenchmarkNode =
    node?.pypType == "benchmark" && node?.parentType == "unit";

  const isInterdisciplinaryObjectivesNode =
    node?.uid == "INTERDISCIPLINARY_OBJECTIVES";

  const isPYPLearningStandardNode = node?.uid == "PYP_LEARNING_STANDARD";
  const isSubjectStandardNode = node?.uid == "SUBJECT_STANDARDS";
  const isAtlClustersNodeNode = node?.uid == "ATL_CLUSTERS";

  switch (true) {
    case isInterdisciplinaryObjectivesNode:
      return 2;

    case isPsuBenchmarkNode:
      return getLevelsBasedOnUptoLevel({
        upToLevel: updatedNodeUpToLevel,
        prefix: "L",
        startFromLevel: 0,
      });

    case isPsuAtlChildrenNode:
    case isPYPLearningStandardNode:
    case isPsAtlsChildrenNode:
      return getLevelsBasedOnUptoLevel({
        upToLevel: updatedNodeUpToLevel,
        prefix: "L",
      });

    case isInterdisciplinaryMYPNode:
    case isSubjectStandardNode:
    case isAtlClustersNodeNode:
      return getLevelsBasedOnUptoLevel({ upToLevel: nodeUpToLevel });

    default:
      return null;
  }
};

export const getFilteredCriteriaSetOfPypType = ({
  node,
  nodes,
  academicCriteriaSets,
  ratingLevels,
  pypType,
  pypSubType,
  parentNode,
  itemId,
  fromTemplateEnd,
  fromReportEnd,
}) => {
  let criteriaSet;

  // For template end we check node.id
  // For report end we check node.pypType and parentNode.pypType
  if (
    (fromTemplateEnd && node?.id == "psuAtls") ||
    (fromReportEnd && node.pypType == "atl" && parentNode.pypType == "unitPlan")
  ) {
    criteriaSet = getNodeChildrenUniqueCriteriaSets({
      node,
      nodes,
      academicCriteriaSets,
      ratingLevels,
      pypType,
      pypSubType,
      parentNode: fromTemplateEnd ? node : parentNode,
    });
  } else if (node?.id == "psAtls") {
    criteriaSet = getNodeChildrenUniqueCriteriaSets({
      node,
      nodes,
      academicCriteriaSets,
      ratingLevels,
      pypType,
      pypSubType,
      parentNode: node,
    });
  } else if (node?.id == "ee=>objectives" || node?.id == "rp=>objectives") {
    criteriaSet = _.filter(academicCriteriaSets, criteriaSet =>
      _.includes(
        CUSTOM_CRITERIA_TYPES_FOR_EE_RP_PROJECTS,
        criteriaSet.criteriaType
      )
    );
  } else if (node.uid == "COMMUNITY_ENGAGEMENT_OUTCOMES") {
    criteriaSet = _.filter(
      academicCriteriaSets,
      set =>
        set.criteriaType === LEARNING_OUTCOME_CRITERIA_TYPE &&
        set.isRatingEnabled
    );
  } else if (
    getValueBasedOnReportType() &&
    (node?.uid === "PP_CRITERIA" || node?.uid === "CP_CRITERIA")
  ) {
    criteriaSet = _.filter(academicCriteriaSets, criteriaSet =>
      _.includes([IB_DEFINED], criteriaSet.criteriaType)
    );
  } else {
    criteriaSet = getCriteriaSetOfPypType({
      academicCriteriaSets,
      ratingLevels,
      parentId:
        node?.uid == "PYP_LEARNING_STANDARD" ? parentNode?.itemId : itemId,
      type: pypElementMapping[pypType],
      subType: pypSubType,
      levelId: getLevelIdForCriteriaSet({ node }),
    });
  }

  return criteriaSet;
};

export const getLevelToCriteriaMap = ({
  levels,
  parentId,
  type,
  ratingLevels,
  criteriaSet,
}) =>
  _.reduce(
    levels,
    (acc, { id: levelId }) => {
      acc[levelId] = _.chain(criteriaSet)
        .filter(({ id: setId }) =>
          checkCriteriaSetOfPypTypeInLevels({
            levelId,
            setId,
            parentId,
            type,
            ratingLevels,
          })
        )
        .map("id")
        .value();
      return acc;
    },
    {}
  );

export const getAcademicCriteriaSetsEnabledForAggregationForTemplateSide = ({
  aggregationType,
  isGPAEnabled,
  isCreditsEnabled,
  isScoreAndGradeAggregationEnabled,
  nodes,
  academicCriteriaSets,
  isGradeAggregationEnabled,
}) => {
  return _.reduce(
    academicCriteriaSets,
    (acc, criteriaSet) => {
      const shouldHideCurrentCriteriaSet = shouldHideAggregationRowOrColumnInTemplate(
        {
          aggregationType,
          isGPAEnabled,
          isCreditsEnabled,
          isScoreAndGradeAggregationEnabled,
          nodes,
          academicCriteriaSets: getValueBasedOnReportType()
            ? { [aggregationType]: [criteriaSet] }
            : [criteriaSet],
          isGradeAggregationEnabled,
        }
      );
      if (!shouldHideCurrentCriteriaSet) {
        acc.push({
          id: _.get(criteriaSet, "id"),
          criteriaParentType: _.get(criteriaSet, "criteriaType"),
          label: _.get(criteriaSet, "label"),
        });
      }
      return acc;
    },
    []
  );
};

export const getAggregatedCriteriaSets = ({ academicCriteriaSets }) => {
  const aggregatedCriteriaSets = _.reduce(
    academicCriteriaSets,
    (acc, criteriaSet) => {
      if (criteriaSet.criteriaType === "AGGREGATE") {
        acc.push({
          ...criteriaSet,
          displaySequence:
            AGGREGATE_SUB_TYPE_DISPLAY_SEQUENCE[criteriaSet.subType],
        });
      }
      return acc;
    },
    []
  );

  return _.sortBy(aggregatedCriteriaSets, "displaySequence");
};

export const getIlDynamicCriteriaTypes = ({
  showOverallGradeForIl,
  showLocalGradeForIl,
}) => {
  const dynamicCriteriaTypes = [];
  if (showOverallGradeForIl) {
    dynamicCriteriaTypes.push("FINAL_GRADE");
  }
  if (showLocalGradeForIl) {
    dynamicCriteriaTypes.push("LOCAL_GRADE");
  }
  return dynamicCriteriaTypes;
};

export const getDynamicCriteriaTypes = ({
  uid,
  showOverallGradeForIl,
  showLocalGradeForIl,
}) => {
  switch (uid) {
    case "INTERDISCIPLINARY_GRADES":
      return getIlDynamicCriteriaTypes({
        showOverallGradeForIl,
        showLocalGradeForIl,
      });
    case "PP_GRADE_SUMMARY":
      return DYNAMIC_CRITERIA_TYPES_FOR_PP_PROJECTS;
    case "CP_GRADE_SUMMARY":
      return DYNAMIC_CRITERIA_TYPES_FOR_CP_PROJECTS;
    case "SL_GRADE_SUMMARY":
    case "CE_GRADE_SUMMARY":
    case "CAS_GRADE_SUMMARY":
      return DYNAMIC_CRITERIA_TYPES_FOR_CAS_SL_PROJECTS;
    case "SA_GRADE_SUMMARY":
      return DYNAMIC_CRITERIA_TYPES_FOR_SA_PROJECTS;
    case "EE_GRADE_SUMMARY":
    case "RP_GRADE_SUMMARY":
      return DYNAMIC_CRITERIA_TYPES_FOR_EE_RP_PROJECTS;
    case "DP_TOK_EXHIBITION_GRADE_SUMMARY":
    case "DP_TOK_ESSAY_GRADE_SUMMARY":
      return DYNAMIC_CRITERIA_TYPES_FOR_TOK_PROJECTS;
    case "TOK_GRADE_SUMMARY":
      return DYNAMIC_CRITERIA_TYPES_FOR_TOK_OVERALL_PROJECTS;
    default:
      return DYNAMIC_CRITERIA_TYPES;
  }
};

export const getProjectObjectivesLabelMapping = ({
  parentNode,
  nodeId,
  index,
  projectType,
}) => {
  const updatedIndex = _.isEmpty(parentNode)
    ? index
    : _.findIndex(parentNode?.children, childId => nodeId == childId);

  // Mapping of criterion labels by project type
  const criterionMappings = {
    PERSONAL: {
      0: "Criterion A: Planning",
      1: "Criterion B: Applying skills",
      2: "Criterion C: Reflecting",
    },
    COMMUNITY: {
      0: "Criterion A: Investigating",
      1: "Criterion B: Planning",
      2: "Criterion C: Taking action",
      3: "Criterion D: Reflecting",
    },
  };

  return _.get(criterionMappings, [projectType, updatedIndex], "");
};

const filterCriteriaSets = ({
  pypType,
  itemId,
  levelId,
  pypSubType,
  ratingLevels,
  criteriaSets,
  criteriaTypesToFilter = [],
  objectiveLabelsToFilter = [],
}) => {
  const type = pypElementMapping[pypType];

  return _.filter(
    criteriaSets,
    ({ id, criteriaType = null, objectiveLabel = "" }) => {
      const isMatchingPypType = checkCriteriaSetOfPypTypeInLevels({
        parentId: itemId,
        setId: id,
        ratingLevels,
        levelId,
        type,
        subType: pypSubType,
      });

      return (
        (isMatchingPypType || criteriaType !== "CUSTOM") &&
        !_.includes(criteriaTypesToFilter, criteriaType) &&
        !_.includes(objectiveLabelsToFilter, objectiveLabel)
      );
    }
  );
};

const getIlCriteriaTypesToFilter = ({
  showOverallGradeForIl,
  showLocalGradeForIl,
}) => {
  const ilCriteriaToFilter = ["FINAL_SCORE"];

  if (!showOverallGradeForIl) {
    ilCriteriaToFilter.push("FINAL_GRADE");
  }
  if (!showLocalGradeForIl) {
    ilCriteriaToFilter.push("LOCAL_GRADE");
  }

  return ilCriteriaToFilter;
};

//This function ensures correct criteria sets are passed to the table based on which node we are processing for the Cumulative Progress Summary Table.

export const getSubjectCriteriaSets = ({
  criteriaSets,
  node,
  ratingLevels,
  subjectNode,
  nodes,
  showOverallGradeForIl,
  showLocalGradeForIl,
  curriculumProgramType,
  isNewGradeScaleSettingsEnabled = false,
  t,
}) => {
  const { uid = null } = node;

  switch (uid) {
    case "INTERDISCIPLINARY_GRADES":
    case "SUBJECT_GRADES": {
      const { pypType, itemId, levelId, pypSubType } =
        curriculumProgramType === CURRICULUM_TYPE_PYP ? subjectNode : node;

      const dynamicCriteriaTypesToFilter =
        uid == "INTERDISCIPLINARY_GRADES"
          ? getIlCriteriaTypesToFilter({
              showOverallGradeForIl,
              showLocalGradeForIl,
            })
          : [];

      return filterCriteriaSets({
        pypType,
        itemId,
        levelId,
        pypSubType,
        ratingLevels,
        criteriaSets,
        criteriaTypesToFilter: [
          "IB_DEFINED",
          "MYP_CRITERIA_FINAL_SCORE",
          ...dynamicCriteriaTypesToFilter,
        ],
      });
    }
    case "SUBJECT_OBJECTIVES":
    case "INTERDISCIPLINARY_OBJECTIVES": {
      const objectiveLabelsToFilter =
        uid == "INTERDISCIPLINARY_OBJECTIVES" ? ["D:"] : [];

      return _.filter(
        criteriaSets,
        ({ criteriaType = null, objectiveLabel = "" }) =>
          _.includes(
            ["IB_DEFINED", "MYP_CRITERIA_FINAL_SCORE"],
            criteriaType
          ) && !_.includes(objectiveLabelsToFilter, objectiveLabel)
      );
    }
    case "PROGRESS_SUMMARY_SUBJECTS": {
      if (subjectNode?.uid === "INTERDISCIPLINARY_LEARNING") {
        const ilGradeNode =
          _.find(nodes, { uid: "INTERDISCIPLINARY_GRADES" }) || {};

        const { pypType, itemId, levelId, pypSubType } = ilGradeNode;
        return filterCriteriaSets({
          pypType,
          itemId,
          levelId,
          pypSubType,
          ratingLevels,
          criteriaSets,
          objectiveLabelsToFilter: ["D:"],
          criteriaTypesToFilter: getIlCriteriaTypesToFilter({
            showOverallGradeForIl,
            showLocalGradeForIl,
          }),
        });
      }
      if (isNewGradeScaleSettingsEnabled) {
        const hasMypIbDefined = _.some(criteriaSets, {
          type: "MYP_IB_DEFINED",
        });

        if (hasMypIbDefined) {
          return _.flatMap(criteriaSets, criteria => {
            if (criteria.type !== "MYP_IB_DEFINED") {
              return criteria;
            }
            return _.map(
              achievementCriterionLabels,
              ({ label, objectiveLabel }) => ({
                ...criteria,
                label: t("progressReport:criterion_with_label", { label }),
                displayName: t("progressReport:criterion_with_label", {
                  label,
                }),
                objectiveLabel,
              })
            );
          });
        }
      }
      return criteriaSets;
    }
    default: {
      return criteriaSets;
    }
  }
};

export const getUpdatedCriteriaSetsForObjectives = ({
  criteriaSet,
  objectiveCriterion,
  isFreezed,
  termIds,
  ratings,
  curriculumType,
  nodeId,
  nodes,
  nodeUid,
  isCompactUi,
  subjectItemId,
  gradingPeriodType,
  isNewGradingSetup,
}) => {
  const { label } = objectiveCriterion;
  const criteriaType =
    objectiveCriterion?.type || objectiveCriterion?.criteriaType;
  if (
    criteriaType === "MYP_CRITERIA_TOTAL" ||
    criteriaType === "MYP_CRITERIA_FINAL_SCORE"
  ) {
    return _.castArray(objectiveCriterion);
  }

  const updatedCriteriaSet = _.map(criteriaSet, criteria => ({
    ...criteria,
    objectiveLabel: label,
  }));
  if (isFreezed) {
    return _.filter(updatedCriteriaSet, criterion => {
      const { id: setId, academicCriteriaValues } = criterion;

      return _.some(
        _.map(termIds, termId => {
          const gradeNodeBasedOnCriteria = getUpdatedGradeNode({
            criterion,
            curriculumType,
            nodeId,
            nodes,
            termId,
            isSingleTermReport: false,
            nodeUid,
            isMYPCriteriaObjective: true,
            isCompactUi,
          });

          const setValue = isNewGradingSetup
            ? _.find(ratings, {
                entityId: gradeNodeBasedOnCriteria?.itemId,
                entityType:
                  pypElementMapping[gradeNodeBasedOnCriteria?.pypType],
                academicTermId: termId,
              })
            : getStudentElementRatingValueOfCriteriaSet({
                type: pypElementMapping[gradeNodeBasedOnCriteria?.pypType],
                nodeId: gradeNodeBasedOnCriteria?.id ?? nodeId,
                itemId: gradeNodeBasedOnCriteria?.itemId ?? subjectItemId,
                ratings,
                termId,
                gradingPeriodType,
                setId,
                checkCategoryId: true,
                categoryId: null,
                shouldCheckComputedRating: false,
              });

          if (isNewGradingSetup) {
            return setValue?.value || false;
          }

          return (
            _.get(
              _.find(academicCriteriaValues, { id: setValue }),
              "abbreviation",
              "-"
            ) != "-"
          );
        })
      );
    });
  }
  return updatedCriteriaSet;
};

export const getCriteriaCommentLabel = ({ t, nodeUid }) => {
  switch (nodeUid) {
    case "SUBJECT_OBJECTIVES":
    case "INTERDISCIPLINARY_OBJECTIVES": {
      return t("common:comment");
    }
    default: {
      return t("progressReport:teacher_comment");
    }
  }
};

export const getCriteriaSetBasedOnCriteriaType = ({
  academicCriteriaSets,
  criteriaType,
}) => {
  return _.find(academicCriteriaSets, { criteriaType });
};

export const getUpdatedLabelsForCriteriaSets = ({
  academicCriteriaSets,
  node,
  t,
}) => {
  return _.map(academicCriteriaSets, criteriaSet => {
    if (
      criteriaSet.criteriaType === MYP_CRITERIA_FINAL_SCORE &&
      node.uid == "PROGRESS_SUMMARY_SUBJECTS"
    ) {
      return {
        ...criteriaSet,
        label: t("progressReport:criteria_total"),
      };
    }

    return criteriaSet;
  });
};

export const getStrandsPlaceHolderDetails = ({ t, node, curriculumType }) => {
  const UID_STRANDS_PLACEHOLDERS_MAP = {
    ATL_CLUSTERS_UBD: [
      { id: "skill1", label: t("common:skill") },
      { id: "skill2", label: t("common:skill") },
      { id: "skill3", label: t("common:skill") },
      { id: "skill4", label: t("common:skill") },
    ],
    ATL_CLUSTERS: [
      { id: "skill1", label: t("common:atls_initials") },
      { id: "skill2", label: t("common:atls_initials") },
      { id: "skill3", label: t("common:atls_initials") },
      { id: "skill4", label: t("common:atls_initials") },
    ],
    COURSE_STANDARDS: [
      { id: "std1", label: t("common:standard") },
      { id: "std2", label: t("common:standard") },
      { id: "std3", label: t("common:standard") },
      { id: "std4", label: t("common:standard") },
    ],
    SUBJECT_STANDARDS: [
      { id: "std1", label: t("common:standard") },
      { id: "std2", label: t("common:standard") },
      { id: "std3", label: t("common:standard") },
      { id: "std4", label: t("common:standard") },
    ],
    LEARNER_PROFILE_ATTRIBUTES: [
      { id: "attr1", label: t("common:attribute") },
      { id: "attr2", label: t("common:attribute") },
      { id: "attr3", label: t("common:attribute") },
      { id: "attr4", label: t("common:attribute") },
    ],
    SUBJECT_ASSESSMENT_OBJECTIVES: [
      { id: "obj1", label: t("common:objective") },
      { id: "obj2", label: t("common:objective") },
      { id: "obj3", label: t("common:objective") },
      { id: "obj4", label: t("common:objective") },
    ],
    PROGRESS_SUMMARY_ATLS: [
      { id: "skill1", label: t("common:atls_initials") },
      { id: "skill2", label: t("common:atls_initials") },
      { id: "skill3", label: t("common:atls_initials") },
      { id: "skill4", label: t("common:atls_initials") },
    ],
  };

  const nodeUid =
    _.includes(["ATL_CLUSTERS", "PROGRESS_SUMMARY_ATLS"], node?.uid) &&
    curriculumType === CURRICULUM_TYPE_UBD
      ? "ATL_CLUSTERS_UBD"
      : node?.uid;

  return UID_STRANDS_PLACEHOLDERS_MAP[nodeUid];
};

export const getCriteriaSetValues = ({
  criteriaSet,
  isNewGradingSetup,
  criteriaType,
}) => {
  if (isNewGradingSetup && criteriaType === "FINAL_GRADE" && criteriaSet) {
    const gradeScaleValues = _.get(
      criteriaSet,
      "gradeScaleEntityDetails[0].gradeScaleValues",
      []
    );
    return transformGradeScaleValuesToCriteriaValues(gradeScaleValues);
  }

  return _.sortBy(_.get(criteriaSet, "academicCriteriaValues", []), [
    "abbreviation",
  ]);
};

const addNonPlaceholderScale = ({
  item,
  gradeScaleMap,
  criteriaSetMap,
  primaryGradeScaleId,
  primaryGradeScale,
  combinedScales,
}) => {
  const gradeScale = gradeScaleMap[item.id];

  if (gradeScale) {
    if (_.size(gradeScale) == 1) {
      combinedScales.push(_.first(gradeScale));
    } else {
      combinedScales.push(
        ..._.filter(gradeScale, scale => !scale.isPrimaryGradeScale)
      );
    }
    return;
  }

  if (criteriaSetMap[item.id]) {
    combinedScales.push(criteriaSetMap[item.id]);
  }
  if (primaryGradeScaleId == item.id) {
    combinedScales.push(primaryGradeScale);
  }
};

const buildCategoryPlaceholderChildren = ({
  selectedConfig,
  categoryPlaceholderIds,
  categoryScorePlaceholder,
  categoryGradePlaceholder,
}) =>
  _.chain(selectedConfig)
    .filter(
      config =>
        _.includes(categoryPlaceholderIds, config.id) && config.isEnabled
    )
    .map(config => {
      let placeholder = null;
      if (config.id === categoryScorePlaceholder?.id) {
        placeholder = categoryScorePlaceholder;
      } else if (config.id === categoryGradePlaceholder?.id) {
        placeholder = categoryGradePlaceholder;
      }

      if (!placeholder) return null;

      const { id, label, criteriaType } = placeholder;

      return { id, label, criteriaType, showInProgressSummary: true };
    })
    .compact()
    .value();

export const getEnabledGradeScalesAndCriteriaSets = ({
  gradeScales = [],
  academicCriteriaSets = [],
  gradeScaleConfiguration,
  settingConfigKey,
  t,
  isReportEnd = false,
}) => {
  const selectedConfig = settingConfigKey
    ? _.get(gradeScaleConfiguration, settingConfigKey, [])
    : [];

  const gradeScaleMap = _.groupBy(gradeScales, "id");
  const criteriaSetMap = _.keyBy(academicCriteriaSets, "id");

  const categoryScorePlaceholder = _.find(
    academicCriteriaSets,
    criteriaSet =>
      criteriaSet.criteriaType === "CATEGORY_PLACEHOLDER" &&
      criteriaSet.subType === "SCORE"
  );

  const categoryGradePlaceholder = _.find(
    academicCriteriaSets,
    criteriaSet =>
      criteriaSet.criteriaType === "CATEGORY_PLACEHOLDER" &&
      criteriaSet.subType === "GRADE"
  );

  const categoryPlaceholderIds = _.compact([
    categoryScorePlaceholder?.id,
    categoryGradePlaceholder?.id,
  ]);

  const combinedScales = [];
  let categoryInserted = false;

  const assessmentCategoriesFromReport = _.filter(
    gradeScales,
    scale => scale?.criteriaType === "ASSESSMENT_CATEGORY"
  );

  const primaryGradeScale = _.find(gradeScales, {
    isPrimaryGradeScale: true,
  });

  const primaryGradeScaleId = primaryGradeScale
    ? primaryGradeScale.primaryGradeScaleId
    : null;

  _.forEach(selectedConfig, item => {
    const isPlaceholder = _.includes(categoryPlaceholderIds, item.id);

    if (isPlaceholder) {
      if (!item.isEnabled || categoryInserted) return;

      if (isReportEnd) {
        combinedScales.push(...assessmentCategoriesFromReport);
      } else {
        const children = buildCategoryPlaceholderChildren({
          selectedConfig,
          categoryPlaceholderIds,
          categoryScorePlaceholder,
          categoryGradePlaceholder,
        });

        if (children.length) {
          combinedScales.push({
            id: "category1",
            label: t("common:category"),
            showInProgressSummary: true,
            children,
          });
        }
      }

      categoryInserted = true;
      return;
    }

    if (!item.isEnabled) return;

    addNonPlaceholderScale({
      item,
      gradeScaleMap,
      criteriaSetMap,
      primaryGradeScaleId,
      primaryGradeScale,
      combinedScales,
    });
  });

  const filteredGradeScales = _.filter(
    combinedScales,
    item => !item.criteriaType && item.id !== "category1"
  );

  const filteredCriteriaSets = _.filter(
    combinedScales,
    item => item.criteriaType || item.id === "category1"
  );

  return {
    combinedScales,
    filteredGradeScales,
    filteredCriteriaSets,
  };
};

export const getFilteredCriteriaSetForObjectiveNode = ({
  criteriaSets,
  pypType,
  defaultValue,
}) => {
  const isObjectiveNode = _.includes(
    [
      "mypObjectives",
      "serviceLearningOutcome",
      "cpRp",
      "cpSlLearningOutcomes",
      "cpCELearningOutcomes",
      "dpCas",
      "dpCasLearningOutcomes",
      "dpCriteria",
      "mypSa",
    ],
    pypType
  );
  return isObjectiveNode
    ? _.filter(criteriaSets, set =>
        _.includes(projectCriteriaTypes, set.criteriaType)
      )
    : defaultValue;
};

export const getFilteredCriteriaSetsForEEAndRPCriteria = ({ criteriaSets }) => {
  return _.filter(criteriaSets, criteriaSet =>
    _.includes(
      CUSTOM_CRITERIA_TYPES_FOR_EE_RP_PROJECTS,
      criteriaSet.criteriaType
    )
  );
};

export const getNodeChildrenUniqueCriteriaSets = ({
  node,
  nodes,
  academicCriteriaSets,
  ratingLevels,
  pypType,
  pypSubType,
  parentNode,
}) => {
  const childrenIds = _.get(node, "children", []);
  const childrenNodes = _.map(childrenIds, id => nodes[id]);

  let criteriaSet = _.reduce(
    childrenNodes,
    (acc, childrenNode) => {
      const childrenNodeItemId = _.get(childrenNode, "itemId", "");

      const childrenCriteriaSet = getCriteriaSetOfPypType({
        academicCriteriaSets,
        ratingLevels,
        parentId: childrenNodeItemId,
        type: pypElementMapping[pypType],
        subType: pypSubType,
        levelId: getLevelIdForCriteriaSet({
          node: childrenNode,
          parentNode,
        }),
      });

      acc.push(...childrenCriteriaSet);

      return acc;
    },
    []
  );

  criteriaSet = _.uniqBy(criteriaSet, "id");

  return criteriaSet;
};
